import type { HfdDk108KnobActionId } from "@eweadn-qiankun/shared-utils";
import { Iconfont } from "../Iconfont";

export const DK108_KNOB_ICON_SIZE = 80;

type Dk108KnobInteractiveIconProps = {
  activeAction?: HfdDk108KnobActionId | null;
  hoveredAction?: HfdDk108KnobActionId | null;
  disabled?: boolean;
  readOnly?: boolean;
  onActionChange?: (action: HfdDk108KnobActionId | null) => void;
  onActionHover?: (action: HfdDk108KnobActionId | null) => void;
};

const ROTATE_ICON_MAP: Record<
  Exclude<HfdDk108KnobActionId, "press">,
  { icon: string; label: string }
> = {
  clockwise: { icon: "rotate2", label: "顺时针旋转" },
  counterClockwise: { icon: "rotate1", label: "逆时针旋转" },
};

function segmentClass(
  actionId: HfdDk108KnobActionId,
  activeAction: HfdDk108KnobActionId | null | undefined,
  hoveredAction: HfdDk108KnobActionId | null | undefined
): string {
  return [
    "dk108-knob-iconfont__btn",
    `dk108-knob-iconfont__btn--${actionId}`,
    activeAction === actionId ? "is-active" : "",
    hoveredAction === actionId ? "is-hovered" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function handleSegmentClick(
  actionId: HfdDk108KnobActionId,
  activeAction: HfdDk108KnobActionId | null | undefined,
  disabled: boolean | undefined,
  readOnly: boolean | undefined,
  onActionChange?: (action: HfdDk108KnobActionId | null) => void
) {
  if (disabled || readOnly || !onActionChange) return;
  onActionChange(activeAction === actionId ? null : actionId);
}

function KnobRotateButton({
  actionId,
  activeAction,
  hoveredAction,
  disabled,
  readOnly,
  onActionChange,
  onActionHover,
}: {
  actionId: Exclude<HfdDk108KnobActionId, "press">;
  activeAction: HfdDk108KnobActionId | null | undefined;
  hoveredAction: HfdDk108KnobActionId | null | undefined;
  disabled: boolean;
  readOnly: boolean;
  onActionChange?: (action: HfdDk108KnobActionId | null) => void;
  onActionHover?: (action: HfdDk108KnobActionId | null) => void;
}) {
  const spec = ROTATE_ICON_MAP[actionId];
  const className = segmentClass(actionId, activeAction, hoveredAction);
  const bindHover = {
    onMouseEnter: () => onActionHover?.(actionId),
    onMouseLeave: () => onActionHover?.(null),
    onFocus: () => onActionHover?.(actionId),
    onBlur: () => onActionHover?.(null),
  };

  if (readOnly) {
    return (
      <div className={className} aria-label={spec.label} {...bindHover}>
        <Iconfont icon={spec.icon} className="dk108-knob-iconfont__glyph" />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-label={spec.label}
      disabled={disabled}
      {...bindHover}
      onClick={() =>
        handleSegmentClick(
          actionId,
          activeAction,
          disabled,
          readOnly,
          onActionChange
        )
      }
    >
      <Iconfont icon={spec.icon} className="dk108-knob-iconfont__glyph" />
    </button>
  );
}

function KnobPressButton({
  activeAction,
  hoveredAction,
  disabled,
  readOnly,
  onActionChange,
  onActionHover,
}: {
  activeAction: HfdDk108KnobActionId | null | undefined;
  hoveredAction: HfdDk108KnobActionId | null | undefined;
  disabled: boolean;
  readOnly: boolean;
  onActionChange?: (action: HfdDk108KnobActionId | null) => void;
  onActionHover?: (action: HfdDk108KnobActionId | null) => void;
}) {
  const className = segmentClass("press", activeAction, hoveredAction);
  const bindHover = {
    onMouseEnter: () => onActionHover?.("press"),
    onMouseLeave: () => onActionHover?.(null),
    onFocus: () => onActionHover?.("press"),
    onBlur: () => onActionHover?.(null),
  };

  const dot = <span className="dk108-knob-iconfont__press-dot" aria-hidden />;

  if (readOnly) {
    return (
      <div className={className} aria-label="按下" {...bindHover}>
        {dot}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-label="按下"
      disabled={disabled}
      {...bindHover}
      onClick={() =>
        handleSegmentClick(
          "press",
          activeAction,
          disabled,
          readOnly,
          onActionChange
        )
      }
    >
      {dot}
    </button>
  );
}

/** DK108 旋钮交互：iconfont rotate1 / rotate2 + 中心 div 圆点 */
export function Dk108KnobInteractiveIcon({
  activeAction = null,
  hoveredAction = null,
  disabled = false,
  readOnly = false,
  onActionChange,
  onActionHover,
}: Dk108KnobInteractiveIconProps) {
  const rotateCommon = {
    activeAction,
    hoveredAction,
    disabled,
    readOnly,
    onActionChange,
    onActionHover,
  };

  return (
    <div
      className={[
        "dk108-knob-iconfont",
        readOnly ? "dk108-knob-iconfont--readonly" : "",
        disabled ? "is-disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden
    >
      <KnobRotateButton {...rotateCommon} actionId="clockwise" />
      <KnobPressButton
        activeAction={activeAction}
        hoveredAction={hoveredAction}
        disabled={disabled}
        readOnly={readOnly}
        onActionChange={onActionChange}
        onActionHover={onActionHover}
      />
      <KnobRotateButton {...rotateCommon} actionId="counterClockwise" />
    </div>
  );
}
